<template>
  <div>
    <v-combobox
      v-model="selected"
      :items="typeItems"
      :label="$t('hospitals.editPopup.types')"
      multiple
      small-chips
      deletable-chips
      @input="onChange"
    />

    <v-card
      v-if="selectedTraumaTypes.length"
      outlined
      class="trauma-section mb-3"
    >
      <div class="trauma-section-label">
        {{ $t('hospitals.editPopup.traumaPopulations') }}
      </div>
      <v-layout
        v-for="traumaType in selectedTraumaTypes"
        :key="traumaType.value"
        align-center
        class="px-3"
      >
        <span class="trauma-type-name">{{ traumaType.text }}</span>
        <v-checkbox
          v-model="traumaType.flags.supportsAdult"
          :label="$t('hospitals.editPopup.supportsAdult')"
          class="ml-4 mt-0"
          hide-details
          @change="onChange"
        />
        <v-checkbox
          v-model="traumaType.flags.supportsPediatric"
          :label="$t('hospitals.editPopup.supportsPediatric')"
          class="ml-4 mt-0"
          hide-details
          @change="onChange"
        />
      </v-layout>
    </v-card>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { FETCH_DATA } from '@/store/HospitalsTypes/actions';

const TRAUMA_CAPABILITY_TYPE = 'T';

export default {
  name: 'HospitalTypesField',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    /**
     * Hospital types with the pivot flags: [{typeID, supportsAdult, supportsPediatric}].
     */
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selected: [],
      flagsMap: {},
      suppressSync: false,
    };
  },
  computed: {
    ...mapState('hospitalsTypes', ['hospitalsTypes']),

    /**
     * Hospital types select items.
     */
    typeItems() {
      return this.hospitalsTypes.map(type => ({
        text: type.name,
        value: type.id,
      }));
    },

    /**
     * Selected trauma types with the reference to their pivot flags.
     */
    selectedTraumaTypes() {
      return this.selected
        .filter(type => this.isTraumaType(type.value) && this.flagsMap[type.value])
        .map(type => ({ ...type, flags: this.flagsMap[type.value] }));
    },
  },
  watch: {
    value(value) {
      if (this.suppressSync) {
        this.suppressSync = false;
        return;
      }
      this.syncFromValue(value);
    },
    hospitalsTypes() {
      this.selected = this.selected.map(type => ({
        text: this.typeName(type.value),
        value: type.value,
      }));
    },
  },
  created() {
    this.$store.dispatch(`hospitalsTypes/${FETCH_DATA}`);
    this.syncFromValue(this.value);
  },
  methods: {
    /**
     * Check whether the hospital type has the trauma capability.
     *
     * @param {Number} typeID - Hospital type identifier.
     *
     * @return {Boolean} True if the type is a trauma type.
     */
    isTraumaType(typeID) {
      const type = this.hospitalsTypes.find(el => el.id === typeID);

      return !!type && type.capabilityType === TRAUMA_CAPABILITY_TYPE;
    },

    /**
     * Build the internal state from the model value.
     *
     * @param {Array} value - Hospital types with the pivot flags.
     */
    syncFromValue(value) {
      this.selected = (value || []).map(type => ({
        text: this.typeName(type.typeID),
        value: type.typeID,
      }));
      this.flagsMap = {};
      (value || []).forEach(type => {
        this.flagsMap[type.typeID] = {
          supportsAdult: !!type.supportsAdult,
          supportsPediatric: !!type.supportsPediatric,
        };
      });
    },

    /**
     * Get the type name by identifier.
     *
     * @param {Number} typeID - Hospital type identifier.
     *
     * @return {String} Type name.
     */
    typeName(typeID) {
      const type = this.hospitalsTypes.find(el => el.id === typeID);

      return type ? type.name : `#${typeID}`;
    },

    /**
     * Emit the normalized model value on user interaction.
     */
    onChange() {
      this.selected.forEach(type => {
        if (this.isTraumaType(type.value) && !this.flagsMap[type.value]) {
          this.$set(this.flagsMap, type.value, {
            supportsAdult: true,
            supportsPediatric: false,
          });
        }
      });

      const value = this.selected.map(type => {
        const result = { typeID: type.value };
        if (this.isTraumaType(type.value)) {
          result.supportsAdult = this.flagsMap[type.value].supportsAdult;
          result.supportsPediatric = this.flagsMap[type.value].supportsPediatric;
        }
        return result;
      });

      this.suppressSync = true;
      this.$emit('input', value);
    },
  },
};
</script>

<style lang="scss" scoped>
.trauma-section {
  position: relative;
  padding: 20px 8px 8px;
}

.trauma-section-label {
  position: absolute;
  top: -10px;
  left: 12px;
  padding: 0 6px;
  background: #fff;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

.trauma-type-name {
  min-width: 220px;
}
</style>
